import { HardDrive, Folder, X } from "lucide-react";
import { Button } from "components/ui/Button";

type Tr = (es: string, en: string) => string;

export type DriveFolderItem = {
  id: number;
  folderId: string;
  folderName: string;
  description?: string;
  fileCount: number;
};

export type PropertyManagerDriveCardProps = {
  tr: Tr;
  driveConnected: boolean;
  driveEmail: string | null;
  driveFolders: DriveFolderItem[];
  driveFilesCount: number;
  driveConnectLoading: boolean;
  driveConnectMessage: string | null;
  drivePickerLoading: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onPickItems: () => void;
  onRemoveFolder: (id: number) => void;
};

function formatFileCount(tr: Tr, count: number): string {
  if (count === 1) {
    return tr("1 archivo", "1 file");
  }
  return tr(`${count} archivos`, `${count} files`);
}

function otherSharedFilesLabel(tr: Tr, count: number): string {
  if (count === 1) {
    return tr(
      "También hay 1 archivo compartido fuera de esas carpetas.",
      "There is also 1 file shared outside those folders.",
    );
  }
  return tr(
    `También hay ${count} archivos compartidos fuera de esas carpetas.`,
    `There are also ${count} files shared outside those folders.`,
  );
}

function filesOnlyLabel(tr: Tr, count: number): string {
  if (count === 1) {
    return tr("1 archivo compartido.", "1 file shared.");
  }
  return tr(`${count} archivos compartidos.`, `${count} files shared.`);
}

export function PropertyManagerDriveCard({
  tr,
  driveConnected,
  driveEmail,
  driveFolders,
  driveFilesCount,
  driveConnectLoading,
  driveConnectMessage,
  drivePickerLoading,
  onConnect,
  onDisconnect,
  onPickItems,
  onRemoveFolder,
}: PropertyManagerDriveCardProps) {
  const busy = driveConnectLoading || drivePickerLoading;
  const countedInFolders = driveFolders.reduce(
    (sum, folder) => sum + (folder.fileCount || 0),
    0,
  );
  const otherSharedFiles = Math.max(0, driveFilesCount - countedInFolders);

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-3.5 sm:p-4">
      <div className="mb-3 flex min-w-0 items-center gap-2.5">
        <HardDrive
          className="h-5 w-5 shrink-0 text-gray-600"
          strokeWidth={2}
          aria-hidden
        />
        <h2 className="text-base font-semibold tracking-tight text-gray-900">
          Google Drive
        </h2>
      </div>
      {driveConnected ? (
        <div className="space-y-3">
          <div className="space-y-1">
            <p className="text-xs leading-snug text-gray-600">
              {tr(
                "Tu cuenta de Google Drive está conectada.",
                "Your Google Drive account is connected.",
              )}
            </p>
            {driveEmail ? (
              <p className="min-w-0 text-base font-semibold tracking-tight text-gray-900">
                {driveEmail}
              </p>
            ) : null}
          </div>
          <p className="text-xs leading-snug text-gray-500">
            {tr(
              "Elegí los archivos de una carpeta (por ejemplo, liquidaciones por unidad). La carpeta se agrega sola a partir de esos archivos.",
              "Pick the files in a folder (e.g. unit statements). The folder is added automatically from those files.",
            )}
          </p>

          <div className="flex flex-wrap gap-2">
            <Button
              className="shrink-0"
              onClick={onPickItems}
              disabled={busy}
              variant="primary"
              size="sm"
            >
              {drivePickerLoading
                ? tr("Abriendo...", "Opening...")
                : tr("Elegir archivos", "Pick files")}
            </Button>
            <Button
              className="shrink-0"
              onClick={onDisconnect}
              disabled={busy}
              variant="secondary"
              size="sm"
            >
              {driveConnectLoading
                ? tr("Desconectando...", "Disconnecting...")
                : tr("Desconectar Drive", "Disconnect Drive")}
            </Button>
          </div>

          {driveFolders.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-gray-700">
                {tr("Carpetas compartidas", "Shared folders")}
              </p>
              <ul className="space-y-1">
                {driveFolders.map((folder) => (
                  <li
                    key={folder.id}
                    className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5"
                  >
                    <Folder className="h-3.5 w-3.5 shrink-0 text-gray-500" />
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
                      {folder.folderName}
                    </span>
                    <span className="shrink-0 text-xs text-gray-500">
                      {formatFileCount(tr, folder.fileCount || 0)}
                    </span>
                    <button
                      type="button"
                      onClick={() => onRemoveFolder(folder.id)}
                      disabled={busy}
                      className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                      aria-label={tr("Quitar carpeta", "Remove folder")}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
              {otherSharedFiles > 0 ? (
                <p className="text-xs text-gray-500">
                  {otherSharedFilesLabel(tr, otherSharedFiles)}
                </p>
              ) : null}
            </div>
          ) : driveFilesCount > 0 ? (
            <p className="text-xs text-gray-500">
              {filesOnlyLabel(tr, driveFilesCount)}
            </p>
          ) : (
            <p className="text-xs text-gray-500">
              {tr(
                "Todavía no hay carpetas seleccionadas.",
                "No folders selected yet.",
              )}
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          <p className="text-sm leading-snug text-gray-600">
            {tr(
              "Conectá Google Drive y después elegí los archivos que Eloria puede usar (por ejemplo, liquidaciones por unidad).",
              "Connect Google Drive, then pick the files Eloria can use (e.g. unit statements).",
            )}
          </p>
          <Button
            className="shrink-0 self-start"
            onClick={onConnect}
            disabled={driveConnectLoading}
            variant="primary"
            size="sm"
          >
            {driveConnectLoading
              ? tr("Conectando...", "Connecting...")
              : tr("Conectar Google Drive", "Connect Google Drive")}
          </Button>
        </div>
      )}
      {driveConnectMessage ? (
        <p className="mt-3 text-xs leading-snug text-gray-600">
          {driveConnectMessage}
        </p>
      ) : null}
    </div>
  );
}
